/**
 * Centralized helpers for status-driven UI styling.
 *
 * Replaces nested ternary expressions across multiple components and
 * keeps color/icon decisions consistent. See PRD CHANGELOG: 2026-04-26.
 */

import { CheckCircle2, AlertCircle, FileText, Clock } from 'lucide-react';

// --------------------------------------------------------------------------
// Wholesale order status colors (used in staff Production view)
// --------------------------------------------------------------------------
const ORDER_STATUS_BADGE = {
  submitted: 'bg-amber-100 text-amber-700',
  pending_approval: 'bg-amber-100 text-amber-800',
  confirmed: 'bg-blue-100 text-blue-700',
  in_production: 'bg-purple-100 text-purple-700',
  ready_for_delivery: 'bg-indigo-100 text-indigo-700',
  out_for_delivery: 'bg-orange-100 text-orange-700',
  delivered: 'bg-green-100 text-green-700',
};

export function getOrderStatusBadgeClass(status) {
  return ORDER_STATUS_BADGE[status] || 'bg-gray-100 text-gray-700';
}

// --------------------------------------------------------------------------
// Client document display (used in client Profile.jsx)
// --------------------------------------------------------------------------
export function getDocumentDisplayConfig(doc) {
  const isExpired = !!doc.is_expired;
  const isAcknowledged = doc.status === 'acknowledged';

  let borderClass;
  let iconBg;
  let iconColor;
  let StatusIcon;

  if (isExpired) {
    borderClass = 'border-red-200 bg-red-50/30 hover:bg-red-50/60';
    iconBg = 'bg-red-100';
    iconColor = 'text-red-600';
    StatusIcon = AlertCircle;
  } else if (isAcknowledged) {
    borderClass = 'border-green-200 bg-green-50/30 hover:bg-green-50/60';
    iconBg = 'bg-green-100';
    iconColor = 'text-green-600';
    StatusIcon = CheckCircle2;
  } else {
    borderClass = 'border-amber-200 bg-amber-50/30 hover:bg-amber-50/60';
    iconBg = 'bg-amber-100';
    iconColor = 'text-amber-600';
    StatusIcon = Clock;
  }

  return { isExpired, isAcknowledged, borderClass, iconBg, iconColor, StatusIcon };
}

// --------------------------------------------------------------------------
// Adjustment request display (used in client + admin Invoices.jsx)
// --------------------------------------------------------------------------
export function getAdjustmentBorderClass(status) {
  if (status === 'approved') return 'border-green-500 bg-green-50';
  if (status === 'rejected') return 'border-red-500 bg-red-50';
  return 'border-purple-500 bg-purple-50';
}

export function getAdjustmentBadgeClass(status) {
  if (status === 'approved') return 'bg-green-100 text-green-800';
  if (status === 'rejected') return 'bg-red-100 text-red-800';
  return 'bg-purple-100 text-purple-800';
}

// --------------------------------------------------------------------------
// Delivery stop display (used in staff Delivery.jsx)
// --------------------------------------------------------------------------
export function getDeliveryStopCardClass({ status, isSelected }) {
  if (status === 'delivered') return 'bg-green-50 border-green-200';
  if (status === 'failed') return 'bg-red-50 border-red-200';
  if (isSelected) return 'bg-orange-50 border-orange-300 cursor-pointer';
  return 'bg-white border-gray-200';
}

export function getDeliveryStopDotClass({ status, isSelected }) {
  if (status === 'delivered') return 'bg-green-500';
  if (status === 'failed') return 'bg-red-500';
  if (isSelected) return 'border-orange-500 bg-orange-500';
  return 'bg-gray-400';
}

// --------------------------------------------------------------------------
// Pricing tier display (used in client Profile.jsx)
// --------------------------------------------------------------------------
export function getPricingTierClass(discountPercent) {
  if (discountPercent > 25) return 'bg-purple-100 text-purple-700 border-purple-200';
  if (discountPercent > 15) return 'bg-blue-100 text-blue-700 border-blue-200';
  if (discountPercent > 0) return 'bg-amber-100 text-amber-700 border-amber-200';
  return 'bg-gray-100 text-gray-700 border-gray-200';
}

// Client account pricing tier badge — guaranteed-returns clients get purple
// regardless of discount %; otherwise color is derived from discount tier.
export function getPricingTierBadgeClass(pricingTier) {
  if (pricingTier?.has_guaranteed_returns) return 'bg-purple-100 text-purple-800';
  const pct = pricingTier?.discount_percent || 0;
  if (pct > 15) return 'bg-blue-100 text-blue-800';
  if (pct > 0) return 'bg-amber-100 text-amber-800';
  return 'bg-gray-100 text-gray-800';
}
